/** Seletor Mês/Semana/Dia (contrato de granularidade, 11/10): regras puras, sem React. */
export type Granularity = "month" | "week" | "day";
export const GRANULARITIES: readonly Granularity[] = ["month", "week", "day"];
export const GRANULARITY_LABEL: Record<Granularity, string> = { month: "Mês", week: "Semana", day: "Dia" };
/** Substantivo para títulos ("Antecipado por mês"). */
export const GRANULARITY_NOUN: Record<Granularity, string> = { month: "mês", week: "semana", day: "dia" };

/** "Dia" só quando o gráfico plota até 92 dias (mesmo limite do BE). */
export const DAY_MAX_DAYS = 92;
export const GRANULARITY_TOO_FINE = "GRANULARITY_TOO_FINE";
export const GRANULARITY_TOO_FINE_MESSAGE = "Período longo demais para ver por dia (máximo de 92 dias). Escolha Semana ou Mês, ou encurte o período.";
export const DAY_HINT_PERIOD = "Dia só para períodos de até 92 dias";
export const dayHintSpan = (days: number) => `Dia só para gráficos de até 92 dias (este cobre ${days} dias)`;

const DAY_MS = 86_400_000;
const isYmd = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s);
const utc = (ymd: string) => Date.UTC(Number(ymd.slice(0, 4)), Number(ymd.slice(5, 7)) - 1, Number(ymd.slice(8, 10)));

export function parseGranularity(v: string | null | undefined): Granularity | null {
  return v && (GRANULARITIES as readonly string[]).includes(v) ? (v as Granularity) : null;
}

/** Dias civis de `from` a `to`, contando os dois extremos; datas inválidas → null. */
export function periodDays(from: string, to: string): number | null {
  if (!isYmd(from) || !isYmd(to)) return null;
  return Math.round((utc(to) - utc(from)) / DAY_MS) + 1;
}

/** Padrão sensato quando o usuário não escolheu: ≤ 31 dias → Dia; ≤ 92 → Semana; senão (ou desconhecido) → Mês. */
export function defaultForSpan(days: number | null): Granularity {
  if (days === null) return "month";
  if (days <= 31) return "day";
  return days <= DAY_MAX_DAYS ? "week" : "month";
}

/** Dia pedido num intervalo > 92 dias cai para Semana, sem 422 visível. Intervalo desconhecido (null) não trava. */
export function effectiveGranularity(requested: Granularity, spanDays: number | null): { value: Granularity; dayDisabled: boolean } {
  const dayDisabled = spanDays !== null && spanDays > DAY_MAX_DAYS;
  return { value: requested === "day" && dayDisabled ? "week" : requested, dayDisabled };
}

const lastDayOfMonth = (ym: string) => new Date(Date.UTC(Number(ym.slice(0, 4)), Number(ym.slice(5, 7)), 0)).getUTCDate();

/** Dias cobertos pelos buckets (`YYYY-MM`, segunda `YYYY-MM-DD` ou dia), do início do 1º ao fim do último. */
export function bucketsSpanDays(buckets: string[], g: Granularity): number | null {
  if (!buckets.length) return null;
  const first = buckets[0];
  const last = buckets[buckets.length - 1];
  if (g === "month") return periodDays(`${first.slice(0, 7)}-01`, `${last.slice(0, 7)}-${String(lastDayOfMonth(last)).padStart(2, "0")}`);
  const span = periodDays(first.slice(0, 10), last.slice(0, 10));
  return span === null ? null : span + (g === "week" ? 6 : 0);
}

/** Mensagem amigável do 422 `GRANULARITY_TOO_FINE` (a do BE, ou a padrão); outro erro → null. */
export function granularityErrorMessage(err: { code?: string | null; message?: string } | null | undefined): string | null {
  if (!err || err.code !== GRANULARITY_TOO_FINE) return null;
  return err.message || GRANULARITY_TOO_FINE_MESSAGE;
}

/** Sufixo do rótulo de ponto parcial (bucket cortado pelo período ou ainda em andamento). */
export const PARTIAL_SUFFIX = " (parcial)";
export const withPartial = (label: string, partial?: boolean) => (partial ? `${label}${PARTIAL_SUFFIX}` : label);

const pad2 = (n: number) => String(n).padStart(2, "0");
const MONTHS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

/** Bucket de uma data `YYYY-MM-DD` no formato do BE: mês `YYYY-MM`, semana = segunda `YYYY-MM-DD`, dia. */
export function bucketOfDate(ymd: string, g: Granularity): string {
  if (g === "month") return ymd.slice(0, 7);
  if (g === "day") return ymd.slice(0, 10);
  const d = new Date(utc(ymd.slice(0, 10)));
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

/** Rótulo curto do eixo: mês "out/26"; semana e dia "dd/mm". */
export function bucketLabel(bucket: string, g: Granularity): string {
  if (g === "month") return `${MONTHS[Number(bucket.slice(5, 7)) - 1]}/${bucket.slice(2, 4)}`;
  return `${bucket.slice(8, 10)}/${bucket.slice(5, 7)}`;
}
